import {
  Flex,
  LightningIcon,
  PlantIcon,
  SparkleIcon,
  StarFilledIcon,
  Typography,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';

import { WizardBrandPreference } from '../../../utils/wizard/sortMapping';
import type { WizardStepProps } from '../../../utils/wizard/types';
import { WizardStepKey } from '../../../utils/wizard/types';

import tileStyles from '../wizardOptionTile.module.css';
import stepStyles from '../wizardStep.module.css';
import styles from './brandStep.module.css';

interface BrandData {
  description: string;
  icon: ReactNode;
  label: string;
  value: string;
}

const BRANDS: BrandData[] = [
  {
    description: 'Best reviewed by customers',
    icon: <StarFilledIcon size="md" mb={4} />,
    label: 'Top-rated brands',
    value: WizardBrandPreference.TopRated,
  },
  {
    description: 'Curated by the store',
    icon: <LightningIcon size="md" mb={4} />,
    label: 'Featured picks',
    value: WizardBrandPreference.FeaturedPicks,
  },
  {
    description: 'Recently stocked items',
    icon: <PlantIcon size="md" color="palm" mb={4} />,
    label: 'Brands with fresh drops',
    value: WizardBrandPreference.FreshDrops,
  },
  {
    description: "We'll pick a mix that matches your selections",
    icon: <SparkleIcon size="md" color="primary" mb={4} />,
    label: 'Surprise me',
    value: WizardBrandPreference.SurpriseMe,
  },
];

interface BrandOptionProps {
  description: string;
  icon: ReactNode;
  label: string;
  onClick: () => void;
  selected: boolean;
}

function BrandOption({
  description,
  icon,
  label,
  onClick,
  selected,
}: BrandOptionProps) {
  const className = clsx(
    tileStyles['tile'],
    stepStyles['option'],
    styles['option'],
    selected && tileStyles['selected']
  );

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      aria-pressed={selected}
    >
      {icon}
      <Typography variant="body-semibold">{label}</Typography>
      <Typography
        variant="small-body-regular"
        color="grays-dark"
        textAlign="center"
      >
        {description}
      </Typography>
    </button>
  );
}

export function BrandStep({ form, updateForm }: WizardStepProps) {
  const selections = form[WizardStepKey.Brand];

  const handleSelect = (value: string) => {
    const isSelected = selections.includes(value);
    updateForm({
      ...form,
      [WizardStepKey.Brand]: isSelected ? [] : [value],
    });
  };

  return (
    <Flex flexDirection="column" alignItems="center" gap={32} width="100%">
      <Flex flexDirection="column" alignItems="center" gap={8}>
        <Typography variant="h2-semibold" textAlign="center">
          Choose your brand preference
        </Typography>
        <Typography textAlign="center">
          Pick one to guide your matches.
        </Typography>
      </Flex>

      <div className={clsx(stepStyles['grid'], styles['grid'])}>
        {BRANDS.map((brand) => {
          const selected = selections.includes(brand.value);

          return (
            <BrandOption
              key={brand.value}
              description={brand.description}
              icon={brand.icon}
              label={brand.label}
              onClick={() => handleSelect(brand.value)}
              selected={selected}
            />
          );
        })}
      </div>
    </Flex>
  );
}
